import type { ChannelId, EmrPlatform, SchedulingSubtype } from "./types";

export type ChannelCatalogEntry = {
  id: ChannelId;
  label: string;
  group: "owned" | "messaging" | "scheduling";
  adapterFlag?: string;
  description: string;
};

export const CHANNEL_CATALOG: ChannelCatalogEntry[] = [
  {
    id: "website",
    label: "Website",
    group: "owned",
    description: "Website chat with an AI Employee that can receive a message and reply.",
  },
  {
    id: "phone",
    label: "Phone / Front Desk",
    group: "messaging",
    adapterFlag: "SITEGLUE_ACTIVE_PHONE_TEST",
    description: "An AI Employee that answers the phone. A published number is not enough.",
  },
  {
    id: "sms",
    label: "Text Messages",
    group: "messaging",
    description: "An AI Employee that receives texts and replies.",
  },
  {
    id: "email",
    label: "Email",
    group: "messaging",
    description: "An AI Employee that receives email and replies.",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    group: "messaging",
    description: "An AI Employee that replies on WhatsApp. A WhatsApp link is not enough.",
  },
  {
    id: "messenger",
    label: "Facebook Messenger",
    group: "messaging",
    description: "An AI Employee that replies on Facebook Messenger. A Messenger link is not enough.",
  },
  {
    id: "browser_call",
    label: "Browser Calls",
    group: "messaging",
    description: "An AI Employee that can take an in-browser call.",
  },
  {
    id: "video",
    label: "Video",
    group: "messaging",
    description: "An AI Employee that can join a video call.",
  },
  {
    id: "scheduling",
    label: "Online Scheduling",
    group: "scheduling",
    description: "An AI Employee that can complete booking. A calendar link is not enough.",
  },
];

export const DEFAULT_SELECTED_CHANNELS: ChannelId[] = [
  "website",
  "phone",
  "sms",
  "email",
];

export const WIZARD_CHANNEL_IDS: ChannelId[] = ["website", "phone", "sms", "email"];

export const SCHEDULING_SUBTYPES: { id: SchedulingSubtype; label: string }[] = [
  { id: "google", label: "Google Calendar" },
  { id: "calendly", label: "Calendly" },
  { id: "emr", label: "EMR" },
  { id: "other", label: "Other" },
];

export const EMR_PLATFORMS: { id: EmrPlatform; label: string }[] = [
  { id: "dentrix", label: "Dentrix" },
  { id: "opendental", label: "Open Dental" },
  { id: "nexhealth", label: "NexHealth" },
  { id: "juvonno", label: "Juvonno" },
  { id: "other", label: "Other" },
];

/** Older scans stored a combined Social Media channel. Keep parsing it. */
const LEGACY_CHANNEL_IDS: ChannelId[] = ["social"];

export function isChannelId(id: string): id is ChannelId {
  return CHANNEL_CATALOG.some((channel) => channel.id === id) || LEGACY_CHANNEL_IDS.includes(id as ChannelId);
}

export function isSchedulingSubtype(id: string): id is SchedulingSubtype {
  return SCHEDULING_SUBTYPES.some((item) => item.id === id);
}

export function isEmrPlatform(id: string): id is EmrPlatform {
  return EMR_PLATFORMS.some((item) => item.id === id);
}

export function channelById(id: string | undefined): ChannelCatalogEntry | undefined {
  return CHANNEL_CATALOG.find((channel) => channel.id === id);
}

export function isActiveAdapterEnabled(flag: string | undefined): boolean {
  if (!flag) return false;
  return process.env[flag] === "1";
}
